import { Modal, ModalProps } from 'antd';
import { useTranslate } from '@tolgee/react';
import { CheckCircleFilled } from '@ant-design/icons';
import { VoucherListPanel } from '@knowledge/Activity/ActivityForm/VoucherConfig/VoucherListPanel';
import { useEffect, useMemo, useState } from 'react';
import { remove } from 'lodash-es';
import { useActivityContext } from '@knowledge/Activity/ActivityContext';

interface VoucherSelectorProps extends ModalProps {
  storeList: Knowledge.Activity.Store[];
  value?: Knowledge.Activity.Voucher[] | null;
  onChange?: (value: Knowledge.Activity.Voucher[]) => void;
}

interface StoreItemProps {
  store: Knowledge.Activity.Store;
  checked?: boolean;
  active?: boolean;
  onActiveChange?: (store: Knowledge.Activity.Store) => void;
}

const StoreItem = (props: StoreItemProps) => {
  const { checked, active, store, onActiveChange } = props;
  const { storeId } = store;
  const { storeMap } = useActivityContext();
  const targetStore = storeMap?.[storeId];
  const name = targetStore?.alias || targetStore?.storeName;

  return (
    <div
      className="relative flex items-center h-[44px] leading-[44px] px-4 border-t-0 border-l-0 border-r-0 border-b border-[var(--fett-border-default)] border-solid cursor-pointer hover:text-primary-hover"
      onClick={() => onActiveChange?.(store)}
    >
      <span className="w-full text-nowrap text-ellipsis whitespace-nowrap overflow-hidden">
        {name}
      </span>
      {checked && <CheckCircleFilled className="!text-success" />}
      {active && <div className="absolute top-0 right-0 w-[3px] bg-primary h-full" />}
    </div>
  );
};

export const VoucherSelector = (props: VoucherSelectorProps) => {
  const { visible, storeList, onCancel, value = [], onChange } = props;
  const { t } = useTranslate();
  const [currentStore, setCurrentStore] = useState(storeList?.[0]);
  const [selection, setSelection] = useState<Knowledge.Activity.Voucher[]>([]);

  const selected = useMemo(() => {
    return selection?.find?.((v) => v.storeId === currentStore?.storeId);
  }, [currentStore, selection]);

  useEffect(() => {
    if (visible) {
      setSelection(value ? [...value] : []);
      setCurrentStore(storeList?.[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <Modal
      {...props}
      destroyOnClose
      visible={visible}
      width={1000}
      bodyStyle={{ padding: 0 }}
      title={t('select_voucher')}
      onCancel={onCancel}
      onOk={() => {
        onChange?.(
          selection.filter(({ storeId }) => !!storeList.find((s) => s.storeId === storeId)),
        );
        // @ts-ignore
        onCancel?.();
      }}
    >
      <div className="flex h-[600px] bg-[var(--fett-erp-card-bg)]">
        <div className="w-44 bg-[var(--fett-bg-panel)] overflow-y-scroll">
          {storeList.map((store) => {
            const { storeId } = store;
            return (
              <StoreItem
                key={storeId}
                store={store}
                checked={!!selection?.find?.((v) => v.storeId === storeId)}
                active={currentStore?.storeId === storeId}
                onActiveChange={setCurrentStore}
              />
            );
          })}
        </div>
        <VoucherListPanel
          store={currentStore}
          value={selected}
          onChange={(selected) => {
            remove(selection, (v) => v.storeId === selected.storeId);
            setSelection([...selection, selected]);
          }}
        />
      </div>
    </Modal>
  );
};
